<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    max-width="1000px"
    scrollable
  >
    <v-card class="d-flex flex-column" style="max-height: 90vh">
      <v-card-title class="primary white--text py-3 d-flex align-center">
        <v-icon left color="white">mdi-history</v-icon>
        <div class="text-h6 font-weight-bold">
          Budjetin muutosloki ja versiohistoria – Vuosi {{ year }}
        </div>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pt-4 flex-grow-1 overflow-y-auto">
        <!-- Info Banner -->
        <v-alert type="info" dense outlined text class="mb-4 text-caption">
          Kaikki budjetin luonnit, tilimuokkaukset, kirjanpitoraporttien tuonnit
          ja tositesiirrot tallentuvat automaattisesti muutoslokiin täytenä
          tilannevedoksena. Voit palata mihin tahansa aiempaan versioon
          klikkaamalla <strong>Palauta tämä versio</strong>.
        </v-alert>

        <!-- Search / Filter -->
        <v-row dense class="mb-2" align="center">
          <v-col cols="12" sm="8">
            <v-text-field
              v-model="searchQuery"
              prepend-inner-icon="mdi-magnify"
              placeholder="Hae tapahtumaa, tekijää tai toimenpidettä..."
              outlined
              dense
              clearable
              hide-details
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="4" class="text-right text-caption grey--text">
            Yhteensä {{ filteredHistory.length }} tallennustapahtumaa
          </v-col>
        </v-row>

        <div v-if="loading" class="text-center py-8">
          <v-progress-circular
            indeterminate
            color="primary"
          ></v-progress-circular>
          <div class="mt-2 text-caption">Ladataan versiohistoriaa...</div>
        </div>

        <div
          v-else-if="filteredHistory.length === 0"
          class="text-center py-10 grey--text"
        >
          <v-icon size="48" color="grey lighten-1">mdi-history</v-icon>
          <div class="mt-2 text-subtitle-1">
            Ei tallennettuja historiatapahtumia
          </div>
          <div class="text-caption">
            Tapahtumia luodaan automaattisesti budjettia tallennettaessa.
          </div>
        </div>

        <!-- Timeline of changes -->
        <v-timeline v-else dense class="pt-2">
          <v-timeline-item
            v-for="item in filteredHistory"
            :key="item.id"
            :color="getActionColor(item.actionType)"
            small
          >
            <v-card outlined class="mb-2">
              <v-card-text class="py-2 px-3">
                <div
                  class="d-flex justify-space-between align-start flex-wrap mb-1"
                >
                  <div>
                    <v-chip
                      x-small
                      :color="getActionColor(item.actionType)"
                      text-color="white"
                      class="font-weight-bold mr-2 text-uppercase"
                    >
                      {{ getActionLabel(item.actionType) }}
                    </v-chip>
                    <span class="font-weight-bold text-subtitle-2">
                      {{ item.summary }}
                    </span>
                  </div>

                  <div class="text-caption grey--text text-right">
                    <v-icon x-small color="grey">mdi-clock-outline</v-icon>
                    {{ formatTimestamp(item.timestamp) }}
                  </div>
                </div>

                <div
                  v-if="item.details"
                  class="text-body-2 grey--text text--darken-2 mb-2"
                >
                  {{ item.details }}
                </div>

                <!-- User & Metrics chips -->
                <div class="d-flex align-center flex-wrap mt-2 pt-2 border-top">
                  <v-chip
                    x-small
                    outlined
                    class="mr-2 mb-1"
                    color="grey darken-2"
                  >
                    <v-icon left x-small>mdi-account</v-icon>
                    {{ item.user?.name || item.user?.email || "Järjestelmä" }}
                  </v-chip>

                  <v-chip
                    v-if="item.metrics?.accountCount !== undefined"
                    x-small
                    outlined
                    class="mr-2 mb-1"
                    color="blue-grey"
                  >
                    <v-icon left x-small>mdi-format-list-bulleted</v-icon>
                    {{ item.metrics.accountCount }} tiliä
                  </v-chip>

                  <v-chip
                    v-if="item.metrics?.transactionCount !== undefined"
                    x-small
                    outlined
                    class="mr-2 mb-1"
                    color="teal"
                  >
                    <v-icon left x-small>mdi-receipt</v-icon>
                    {{ item.metrics.transactionCount }} tositetta
                  </v-chip>

                  <v-chip
                    v-if="item.metrics?.incomeArvio !== undefined"
                    x-small
                    outlined
                    class="mr-2 mb-1"
                    color="green darken-1"
                  >
                    Tuotot: {{ formatCurrency(item.metrics.incomeArvio) }}
                  </v-chip>

                  <v-chip
                    v-if="item.metrics?.expenseArvio !== undefined"
                    x-small
                    outlined
                    class="mr-2 mb-1"
                    color="red darken-1"
                  >
                    Kulut: {{ formatCurrency(item.metrics.expenseArvio) }}
                  </v-chip>

                  <v-spacer></v-spacer>

                  <!-- Action Buttons -->
                  <v-btn
                    x-small
                    text
                    color="primary"
                    class="mr-1 mb-1"
                    @click="openPreview(item)"
                  >
                    <v-icon left x-small>mdi-eye</v-icon>
                    Esikatsele
                  </v-btn>

                  <v-btn
                    v-if="canManage"
                    x-small
                    color="deep-purple"
                    dark
                    class="mb-1"
                    @click="initiateRestore(item)"
                  >
                    <v-icon left x-small>mdi-restore</v-icon>
                    Palauta tämä versio
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>
          </v-timeline-item>
        </v-timeline>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="py-2 px-4">
        <v-spacer></v-spacer>
        <v-btn text @click="$emit('input', false)">Sulje</v-btn>
      </v-card-actions>
    </v-card>

    <!-- Preview Modal -->
    <v-dialog v-model="previewDialog" max-width="850px" scrollable>
      <v-card v-if="previewItem">
        <v-card-title class="grey lighten-4 py-3 d-flex align-center">
          <v-icon left color="primary">mdi-eye</v-icon>
          <div class="text-subtitle-1 font-weight-bold">
            Version esikatselu: {{ formatTimestamp(previewItem.timestamp) }}
          </div>
          <v-spacer></v-spacer>
          <v-btn icon small @click="previewDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <v-row dense class="mb-3">
            <v-col cols="6">
              <div class="text-caption grey--text">Toimenpide</div>
              <div class="font-weight-bold">{{ previewItem.summary }}</div>
            </v-col>
            <v-col cols="6">
              <div class="text-caption grey--text">Tekijä</div>
              <div>
                {{
                  previewItem.user?.name ||
                  previewItem.user?.email ||
                  "Tuntematon"
                }}
              </div>
            </v-col>
          </v-row>

          <v-card outlined class="mb-4 pa-3 grey lighten-5">
            <div class="text-subtitle-2 font-weight-bold mb-2">
              Yhteenveto tilannevedoksesta:
            </div>
            <v-row dense class="text-caption">
              <v-col cols="6" sm="3">
                <div class="grey--text">Kustannuspaikkoja</div>
                <div class="text-body-2 font-weight-bold">
                  {{ previewItem.budgetSnapshot?.projects?.length || 0 }} kpl
                </div>
              </v-col>
              <v-col cols="6" sm="3">
                <div class="grey--text">Tilejä yhteensä</div>
                <div class="text-body-2 font-weight-bold">
                  {{ previewItem.metrics?.accountCount || 0 }} kpl
                </div>
              </v-col>
              <v-col cols="6" sm="3">
                <div class="grey--text">Tuottoarvio</div>
                <div class="text-body-2 font-weight-bold green--text">
                  {{ formatCurrency(previewItem.metrics?.incomeArvio) }}
                </div>
              </v-col>
              <v-col cols="6" sm="3">
                <div class="grey--text">Kuluarvio</div>
                <div class="text-body-2 font-weight-bold red--text">
                  {{ formatCurrency(previewItem.metrics?.expenseArvio) }}
                </div>
              </v-col>
            </v-row>
          </v-card>

          <div class="text-subtitle-2 font-weight-bold mb-2">
            Kustannuspaikat ja tilit:
          </div>
          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">Kustannuspaikka</th>
                  <th class="text-right">Tilit</th>
                  <th class="text-right">Tuottoarvio</th>
                  <th class="text-right">Kuluarvio</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="p in previewItem.budgetSnapshot?.projects || []"
                  :key="p.id"
                >
                  <td class="font-weight-bold">{{ p.fullName || p.name }}</td>
                  <td class="text-right">
                    {{ (p.accounts || []).length }} kpl
                  </td>
                  <td class="text-right green--text">
                    {{ formatCurrency(calcProjectIncome(p)) }}
                  </td>
                  <td class="text-right red--text">
                    {{ formatCurrency(calcProjectExpense(p)) }}
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="py-2 px-4">
          <v-spacer></v-spacer>
          <v-btn text @click="previewDialog = false">Sulje</v-btn>
          <v-btn
            v-if="canManage"
            color="deep-purple"
            dark
            @click="
              initiateRestore(previewItem);
              previewDialog = false;
            "
          >
            <v-icon left small>mdi-restore</v-icon>
            Palauta tämä versio
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirm Rollback Dialog -->
    <v-dialog v-model="confirmRollbackDialog" max-width="500px">
      <v-card v-if="historyToRestore">
        <v-card-title class="error white--text py-3">
          <v-icon left color="white">mdi-alert</v-icon>
          Vahvista version palautus
        </v-card-title>
        <v-card-text class="pt-4">
          <div class="text-body-1 font-weight-bold mb-2">
            Haluatko varmasti palauttaa budjetin tilaan:
          </div>
          <v-alert dense outlined color="deep-purple" class="mb-3">
            <div class="font-weight-bold">{{ historyToRestore.summary }}</div>
            <div class="text-caption grey--text">
              Aikaleima: {{ formatTimestamp(historyToRestore.timestamp) }}
              <br />Tekijä: {{ historyToRestore.user?.name || "Tuntematon" }}
            </div>
          </v-alert>

          <p class="text-caption grey--text text--darken-2">
            <v-icon x-small color="green darken-2">mdi-shield-check</v-icon>
            <strong>Turvallinen palautus:</strong> Nykyisestä tilasta luodaan
            automaattisesti varmuuskopio historiatietoihin ennen palautusta,
            joten et menetä mitään ja voit tarvittaessa palata takaisin.
          </p>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="py-2 px-4">
          <v-spacer></v-spacer>
          <v-btn
            text
            :disabled="restoring"
            @click="confirmRollbackDialog = false"
          >
            Peruuta
          </v-btn>
          <v-btn
            color="deep-purple"
            dark
            :loading="restoring"
            @click="executeRestore"
          >
            Palauta versio
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import {
  subscribeBudgetHistory,
  restoreBudgetVersion,
} from "@/services/budgetService";

export default {
  name: "BudgetHistoryDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    year: {
      type: [Number, String],
      required: true,
    },
    canManage: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      historyList: [],
      unsubHistory: null,
      searchQuery: "",
      previewDialog: false,
      previewItem: null,
      confirmRollbackDialog: false,
      historyToRestore: null,
      restoring: false,
    };
  },
  computed: {
    filteredHistory() {
      if (!this.searchQuery) return this.historyList;
      const q = this.searchQuery.toLowerCase().trim();
      return this.historyList.filter((item) => {
        const summary = (item.summary || "").toLowerCase();
        const details = (item.details || "").toLowerCase();
        const userName = (item.user?.name || "").toLowerCase();
        const userEmail = (item.user?.email || "").toLowerCase();
        const actionType = (item.actionType || "").toLowerCase();
        return (
          summary.includes(q) ||
          details.includes(q) ||
          userName.includes(q) ||
          userEmail.includes(q) ||
          actionType.includes(q)
        );
      });
    },
  },
  watch: {
    value(val) {
      if (val) {
        this.startSubscription();
      } else {
        this.stopSubscription();
      }
    },
    year() {
      if (this.value) {
        this.startSubscription();
      }
    },
  },
  beforeDestroy() {
    this.stopSubscription();
  },
  methods: {
    startSubscription() {
      this.stopSubscription();
      if (!this.year) return;
      this.loading = true;
      this.unsubHistory = subscribeBudgetHistory(this.year, (items) => {
        this.historyList = items || [];
        this.loading = false;
      });
    },
    stopSubscription() {
      if (this.unsubHistory) {
        this.unsubHistory();
        this.unsubHistory = null;
      }
    },
    formatTimestamp(isoStr) {
      if (!isoStr) return "-";
      const d = new Date(isoStr);
      if (isNaN(d.getTime())) return isoStr;
      return d.toLocaleString("fi-FI", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    formatCurrency(val) {
      if (val === null || val === undefined || isNaN(val)) return "0,00 €";
      return (
        Number(val).toLocaleString("fi-FI", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + " €"
      );
    },
    getActionColor(type) {
      switch (type) {
        case "CREATE":
          return "teal";
        case "ACCOUNT_ADD":
          return "green";
        case "TITLE_ADD":
          return "cyan darken-1";
        case "ACCOUNT_UPDATE":
          return "amber darken-2";
        case "TITLE_UPDATE":
          return "amber darken-3";
        case "ACCOUNT_DELETE":
          return "red";
        case "TITLE_DELETE":
          return "red darken-1";
        case "CSV_IMPORT":
          return "blue darken-1";
        case "TRANSACTION_UPDATE":
          return "orange darken-2";
        case "TRANSACTION_DELETE":
          return "red darken-2";
        case "RESTORE":
          return "deep-purple";
        case "AUTO_BACKUP":
          return "blue-grey";
        default:
          return "primary";
      }
    },
    getActionLabel(type) {
      switch (type) {
        case "CREATE":
          return "Alustus";
        case "ACCOUNT_ADD":
          return "Uusi tili";
        case "TITLE_ADD":
          return "Uusi otsikko";
        case "ACCOUNT_UPDATE":
          return "Muokkaus";
        case "TITLE_UPDATE":
          return "Otsikon muokkaus";
        case "ACCOUNT_DELETE":
          return "Poisto";
        case "TITLE_DELETE":
          return "Otsikon poisto";
        case "CSV_IMPORT":
          return "CSV-tuonti";
        case "TRANSACTION_UPDATE":
          return "Tositesiirto";
        case "TRANSACTION_DELETE":
          return "Tosite poistettu";
        case "RESTORE":
          return "Versiopalautus";
        case "AUTO_BACKUP":
          return "Varmuuskopio";
        default:
          return "Muutos";
      }
    },
    openPreview(item) {
      this.previewItem = item;
      this.previewDialog = true;
    },
    calcProjectIncome(project) {
      return (project.accounts || []).reduce((sum, a) => {
        if (!a.isTotal && !a.isTitle && !a.isHeader && a.type === "income") {
          return sum + (Number(a.arvio) || 0);
        }
        return sum;
      }, 0);
    },
    calcProjectExpense(project) {
      return (project.accounts || []).reduce((sum, a) => {
        if (!a.isTotal && !a.isTitle && !a.isHeader && a.type === "expense") {
          return sum + (Number(a.arvio) || 0);
        }
        return sum;
      }, 0);
    },
    initiateRestore(item) {
      this.historyToRestore = item;
      this.confirmRollbackDialog = true;
    },
    async executeRestore() {
      if (!this.historyToRestore) return;
      this.restoring = true;
      try {
        await restoreBudgetVersion(this.year, this.historyToRestore.id);
        this.$toast?.success?.(
          "Budjetti palautettu onnistuneesti valittuun versioon!",
        );
        this.confirmRollbackDialog = false;
        this.historyToRestore = null;
        this.$emit("restored");
      } catch (err) {
        console.error("Error restoring budget version:", err);
        this.$toast?.error?.(
          "Palautus epäonnistui: " + (err.message || "Tuntematon virhe"),
        );
      } finally {
        this.restoring = false;
      }
    },
  },
};
</script>

<style scoped>
.border-top {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}
</style>
